import React, { useRef, useState, useEffect, useMemo, useCallback, memo } from 'react';
import { Stage, Layer, Group, Rect, Text, Line } from 'react-konva';
import { differenceInDays, startOfToday } from 'date-fns';
import { SchedulerGrid } from './SchedulerGrid';
import { TimeHeader } from './TimeHeader';
import { useSchedulerDimensions } from './useSchedulerDimensions';
import { Bar, Track } from './schedulerTypes';
import { useTranslation } from '@/i18n/useTranslation';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Input } from '@/components/ui/input';
import { SchedulerBar } from './SchedulerBar';
import { CrosshairIndicator } from './CrosshairIndicator';

interface CanvasSchedulerProps {
  startDate: Date;
  endDate: Date;
  tracks: Track[];
  bars: Bar[];
  isLoading?: boolean;
  barOffsetHeight?: number;
  onBarUpdated?: (updatedBar: Bar) => void;
  onGridClicked?: (trackId: string, date: Date) => void;
  onBarTextClicked?: (bar: Bar) => void;
  onTodayButtonRef?: (ref: HTMLButtonElement | null) => void;
}

const getBarOffsets = (bars: Bar[], barOffsetHeight: number): Map<string, number> => {
  const offsets = new Map<string, number>();
  
  const trackBars = new Map<string, Bar[]>();
  bars.forEach(bar => {
    if (!trackBars.has(bar.trackId)) {
      trackBars.set(bar.trackId, []);
    }
    trackBars.get(bar.trackId)!.push(bar);
  });
  
  trackBars.forEach((trackBarsList, trackId) => {
    const sortedBars = [...trackBarsList].sort((a, b) => {
      const durationA = differenceInDays(a.endDate, a.startDate);
      const durationB = differenceInDays(b.endDate, b.startDate);
      return durationB - durationA;
    });
    
    const dayOccupancy = new Map<number, number[]>();
    
    sortedBars.forEach(bar => {
      const startDay = differenceInDays(bar.startDate, new Date(bar.startDate.getFullYear(), 0, 1));
      const endDay = differenceInDays(bar.endDate, new Date(bar.endDate.getFullYear(), 0, 1));
      
      let level = 0;
      let levelFound = false;
      
      while (!levelFound) {
        levelFound = true;
        
        for (let day = startDay; day <= endDay; day++) {
          if (!dayOccupancy.has(day)) {
            dayOccupancy.set(day, []);
          }
          
          const occupiedLevels = dayOccupancy.get(day)!;
          
          if (occupiedLevels.includes(level)) {
            levelFound = false;
            level++;
            break;
          }
        }
      }
      
      offsets.set(bar.id, level * barOffsetHeight);
      
      for (let day = startDay; day <= endDay; day++) {
        const occupiedLevels = dayOccupancy.get(day)!;
        occupiedLevels.push(level);
      }
    });
  });
  
  return offsets;
};

const getMaxTrackOffsets = (bars: Bar[], barOffsets: Map<string, number>): Map<string, number> => {
  const trackMaxOffsets = new Map<string, number>();
  
  bars.forEach(bar => {
    const offset = barOffsets.get(bar.id) || 0;
    const currentMax = trackMaxOffsets.get(bar.trackId) || 0;
    if (offset > currentMax) {
      trackMaxOffsets.set(bar.trackId, offset);
    }
  });
  
  return trackMaxOffsets;
};

const ControlPanel = memo(({ 
  dayWidth, 
  onDayWidthChange, 
  onTodayClick, 
  t,
  todayButtonRef
}: { 
  dayWidth: number, 
  onDayWidthChange: (width: number[]) => void, 
  onTodayClick: () => void,
  t: (key: string) => string,
  todayButtonRef?: (ref: HTMLButtonElement | null) => void
}) => (
  <div className="flex justify-end items-center mb-2 px-4 gap-4">
    <div className="flex items-center gap-2">
      <span className="text-sm text-gray-500">{t('Day Width')}:</span>
      <div className="w-40">
        <Slider
          value={[dayWidth]}
          min={20}
          max={80}
          step={5}
          onValueChange={onDayWidthChange}
        />
      </div>
      <Input 
        type="number" 
        value={dayWidth}
        onChange={(e) => onDayWidthChange([Number(e.target.value)])}
        className="w-16 h-8 text-sm"
        min={20}
        max={80}
      />
      <span className="text-sm">px</span>
    </div>
    <Button 
      onClick={onTodayClick} 
      variant="outline" 
      size="sm"
      ref={todayButtonRef}
    >
      {t('Today')}
    </Button>
  </div>
));

const TrackContent = memo(({ 
  track, 
  trackY, 
  trackBars, 
  dimensions, 
  hoveredCell,
  startDate,
  handleBarSelect,
  handleBarChange,
  handleBarResizeComplete,
  handleBarTextClick,
  handleCellHover,
  handleCellClick,
  selectedBarId,
  barOffsets,
  trackMaxOffset
}: { 
  track: Track,
  trackY: number,
  trackBars: Bar[],
  dimensions: any,
  hoveredCell: any,
  startDate: Date,
  handleBarSelect: (barId: string) => void,
  handleBarChange: (updatedBar: Bar) => void,
  handleBarResizeComplete: (updatedBar: Bar) => void,
  handleBarTextClick: (bar: Bar) => void,
  handleCellHover: (trackId: string, dayIndex: number) => void,
  handleCellClick: (trackId: string, date: Date) => void,
  selectedBarId: string | null,
  barOffsets: Map<string, number>,
  trackMaxOffset: number
}) => {
  const sortedBars = [...trackBars].sort((a, b) => {
    const offsetA = barOffsets.get(a.id) || 0;
    const offsetB = barOffsets.get(b.id) || 0;
    return offsetA - offsetB;
  });

  const isTrackHighlighted = hoveredCell.trackId === track.id;
  const adjustedTrackHeight = dimensions.trackHeight + trackMaxOffset;
  
  return (
    <div 
      style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        height: adjustedTrackHeight,
        margin: 0,
        padding: 0
      }}
    >
      <div 
        style={{
          position: 'sticky',
          left: 0,
          width: dimensions.trackHeaderWidth,
          height: '100%',
          backgroundColor: isTrackHighlighted ? '#e5f6ff' : '#f9fafb',
          borderRight: '1px solid #e5e7eb',
          zIndex: 1,
          display: 'flex',
          alignItems: 'center',
          paddingLeft: '10px',
          transition: 'background-color 0.2s ease',
          margin: 0
        }}
      >
        <span style={{ 
          fontSize: '12px', 
          fontFamily: 'sans-serif', 
          whiteSpace: 'nowrap', 
          overflow: 'hidden', 
          textOverflow: 'ellipsis' 
        }}>
          {track.name}
        </span>
      </div>

      <div 
        style={{
          flex: 1,
          height: '100%',
          position: 'relative',
          margin: 0,
          padding: 0
        }}
      >
        <Stage 
          width={dimensions.gridWidth} 
          height={adjustedTrackHeight}
        >
          <Layer>
            <SchedulerGrid
              startDate={startDate}
              endDate={new Date(startDate.getTime() + (dimensions.gridWidth / dimensions.dayWidth) * 86400000)}
              dayWidth={dimensions.dayWidth}
              trackHeight={adjustedTrackHeight}
              trackY={0}
              trackId={track.id}
              onCellHover={handleCellHover}
              hoveredDayIndex={hoveredCell.dayIndex}
              hoveredTrackId={hoveredCell.trackId}
              onCellClick={handleCellClick}
            />

            <Line
              points={[0, adjustedTrackHeight, dimensions.gridWidth, adjustedTrackHeight]}
              stroke="#e5e7eb"
              strokeWidth={1}
            />

            {sortedBars.map(bar => (
              <SchedulerBar
                key={bar.id}
                bar={bar}
                schedulerStartDate={startDate}
                dayWidth={dimensions.dayWidth}
                trackHeight={dimensions.trackHeight}
                isSelected={bar.id === selectedBarId}
                onSelect={() => handleBarSelect(bar.id)}
                onChange={handleBarChange}
                onResizeComplete={handleBarResizeComplete}
                onBarTextClick={handleBarTextClick}
                verticalOffset={barOffsets.get(bar.id) || 0}
              />
            ))}
          </Layer>
        </Stage>
      </div>
    </div>
  );
});

const CategorySection = memo(({ 
  category, 
  categoryTracks, 
  categoryY, 
  categoryIndex, 
  dimensions, 
  filterBarsForTrack, 
  handleCellHover, 
  handleCellClick, 
  hoveredCell, 
  startDate, 
  selectedBarId, 
  handleBarSelect, 
  handleBarChange, 
  handleBarResizeComplete, 
  handleBarTextClick, 
  CATEGORY_HEADER_HEIGHT,
  barOffsets,
  trackMaxOffsets
}: {
  category: string,
  categoryTracks: Track[],
  categoryY: number,
  categoryIndex: number,
  dimensions: any,
  filterBarsForTrack: (trackId: string) => Bar[],
  handleCellHover: (trackId: string, dayIndex: number) => void,
  handleCellClick: (trackId: string, date: Date) => void,
  hoveredCell: any,
  startDate: Date,
  selectedBarId: string | null,
  handleBarSelect: (barId: string) => void,
  handleBarChange: (updatedBar: Bar) => void,
  handleBarResizeComplete: (updatedBar: Bar) => void,
  handleBarTextClick: (bar: Bar) => void,
  CATEGORY_HEADER_HEIGHT: number,
  barOffsets: Map<string, number>,
  trackMaxOffsets: Map<string, number>
}) => {
  return (
    <div key={category} style={{ position: 'relative', margin: 0, padding: 0 }}>
      <div 
        style={{
          position: 'sticky',
          top: dimensions.timeHeaderHeight,
          left: 0,
          zIndex: 2,
          backgroundColor: '#e5e7eb',
          height: CATEGORY_HEADER_HEIGHT,
          width: dimensions.totalWidth,
          display: 'flex',
          alignItems: 'center',
          borderBottom: '1px solid #d1d5db',
          margin: 0,
          padding: 0
        }}
      >
        <div 
          style={{
            position: 'sticky',
            left: 0,
            width: dimensions.trackHeaderWidth,
            backgroundColor: '#e5e7eb',
            display: 'flex',
            alignItems: 'center',
            paddingLeft: '10px',
            height: '100%',
            zIndex: 3,
            margin: 0
          }}
        >
          <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#374151' }}>
            {category}
          </span>
        </div>
      </div>

      {categoryTracks.map((track, trackIndex) => {
        let trackY = categoryY + CATEGORY_HEADER_HEIGHT;
        for (let i = 0; i < trackIndex; i++) {
          const previousTrack = categoryTracks[i];
          const previousTrackOffset = trackMaxOffsets.get(previousTrack.id) || 0;
          trackY += dimensions.trackHeight + previousTrackOffset;
        }
        
        const trackBars = filterBarsForTrack(track.id);
        const trackMaxOffset = trackMaxOffsets.get(track.id) || 0;

        return (
          <TrackContent 
            key={track.id}
            track={track}
            trackY={trackY}
            trackBars={trackBars}
            dimensions={dimensions}
            hoveredCell={hoveredCell}
            startDate={startDate}
            handleBarSelect={handleBarSelect}
            handleBarChange={handleBarChange}
            handleBarResizeComplete={handleBarResizeComplete}
            handleBarTextClick={handleBarTextClick}
            handleCellHover={handleCellHover}
            handleCellClick={handleCellClick}
            selectedBarId={selectedBarId}
            barOffsets={barOffsets}
            trackMaxOffset={trackMaxOffset}
          />
        );
      })}
    </div>
  );
});

export const CanvasScheduler = memo(({
  startDate,
  endDate,
  tracks,
  bars,
  isLoading = false,
  barOffsetHeight = 20,
  onBarUpdated,
  onGridClicked,
  onBarTextClicked,
  onTodayButtonRef
}: CanvasSchedulerProps) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [selectedBarId, setSelectedBarId] = useState<string | null>(null);
  const [containerElement, setContainerElement] = useState<HTMLDivElement | null>(null);
  const [dayWidth, setDayWidth] = useState<number>(40);
  const [hoveredCell, setHoveredCell] = useState<{ trackId: string | null; dayIndex: number | null }>({
    trackId: null,
    dayIndex: null,
  });

  useEffect(() => {
    if (containerRef.current) {
      setContainerElement(containerRef.current);
    }
  }, []);

  const barOffsets = useMemo(() => getBarOffsets(bars, barOffsetHeight), [bars, barOffsetHeight]);
  
  const trackMaxOffsets = useMemo(() => 
    getMaxTrackOffsets(bars, barOffsets), [bars, barOffsets]);

  const groupedTracks = useMemo(() => {
    const map = new Map<string, Track[]>();
    tracks.forEach(track => {
      const category = track.category;
      if (!map.has(category)) {
        map.set(category, []);
      }
      map.get(category)?.push(track);
    });
    return map;
  }, [tracks]);

  const totalTrackHeightAdjustment = useMemo(() => {
    let total = 0;
    tracks.forEach(track => {
      total += trackMaxOffsets.get(track.id) || 0;
    });
    return total;
  }, [tracks, trackMaxOffsets]);

  const CATEGORY_HEADER_HEIGHT = 30;
  const categoryHeadersHeight = useMemo(() => 
    groupedTracks.size * CATEGORY_HEADER_HEIGHT, [groupedTracks.size]);

  const dimensions = useSchedulerDimensions(
    containerElement, 
    startDate, 
    endDate,
    tracks.length,
    categoryHeadersHeight,
    dayWidth
  );

  const filterBarsForTrack = useCallback((trackId: string) => {
    return bars.filter(bar => bar.trackId === trackId);
  }, [bars]);

  const handleTodayButtonClick = useCallback(() => {
    if (containerRef.current && dimensions) {
      const today = startOfToday();
      const daysFromStart = differenceInDays(today, startDate);
      const todayPosition = daysFromStart * dimensions.dayWidth;
      const gridContainerWidth = containerRef.current.clientWidth - dimensions.trackHeaderWidth || 0;
      const scrollPosition = Math.max(0, todayPosition - (gridContainerWidth / 2));
      
      if (containerRef.current) {
        containerRef.current.scrollLeft = scrollPosition;
      }
    }
  }, [dimensions, startDate]);

  const handleDayWidthChange = useCallback((value: number[]) => {
    const newWidth = value[0];
    setDayWidth(newWidth);
  }, []);

  const handleBarChange = useCallback((updatedBar: Bar) => {
    // This is just a temporary update for the UI
    // The actual update is handled by handleBarResizeComplete
  }, []);

  const handleBarResizeComplete = useCallback((updatedBar: Bar) => {
    setSelectedBarId(null); // Deselect the bar after resizing

    if (onBarUpdated) {
      onBarUpdated(updatedBar); // Notify parent about the updated bar
    }
  }, [onBarUpdated]);

  const handleCellHover = useCallback((trackId: string, dayIndex: number) => {
    setHoveredCell({ trackId, dayIndex });
  }, []);

  const handleGridMouseLeave = useCallback(() => {
    setHoveredCell({ trackId: null, dayIndex: null });
  }, []);

  const handleCellClick = useCallback((trackId: string, date: Date) => {
    if (onGridClicked) {
      onGridClicked(trackId, date);
    }
  }, [onGridClicked]);

  const handleBarTextClick = useCallback((bar: Bar) => {
    if (onBarTextClicked) {
      onBarTextClicked(bar);
    }
  }, [onBarTextClicked]);

  const handleBarSelect = useCallback((barId: string) => {
    setSelectedBarId(barId);
  }, []);

  if (isLoading) {
    return <div className="w-full h-full flex items-center justify-center">{t('Loading...')}</div>;
  }

  if (!dimensions) {
    return (
      <div 
        ref={containerRef} 
        className="w-full h-full flex items-center justify-center"
      >
        {t('Initializing scheduler...')}
      </div>
    );
  }

  const adjustedDimensions = {
    ...dimensions,
    totalHeight: dimensions.totalHeight + totalTrackHeightAdjustment
  };

  return (
    <div className="w-full h-full flex flex-col">
      <ControlPanel 
        dayWidth={dayWidth} 
        onDayWidthChange={handleDayWidthChange} 
        onTodayClick={handleTodayButtonClick}
        t={t}
        todayButtonRef={onTodayButtonRef}
      />
      
      <div
        ref={containerRef} 
        className="w-full flex-1 overflow-auto"
        style={{ 
          position: 'relative',
          minHeight: '500px',
          margin: 0,
          padding: 0
        }}
        onMouseLeave={handleGridMouseLeave}
        data-scheduler='scrollableViewport'
      >
        <div 
          style={{ 
            position: 'sticky', 
            top: 0, 
            zIndex: 3,
            height: dimensions.timeHeaderHeight,
            width: '100%',
            margin: 0,
            padding: 0
          }}
        >
          <div 
            style={{ 
              position: 'absolute', 
              left: 0,
              width: dimensions.trackHeaderWidth,
              height: dimensions.timeHeaderHeight,
              backgroundColor: '#f9fafb',
              zIndex: 3,
              boxShadow: '2px 0 5px rgba(0,0,0,0.1)',
              margin: 0,
              padding: 0
            }}
          />
          <div
            style={{ 
              position: 'absolute', 
              left: dimensions.trackHeaderWidth, 
              width: dimensions.gridWidth, 
              height: dimensions.timeHeaderHeight,
              backgroundColor: '#ffffff',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              zIndex: 2,
              margin: 0,
              padding: 0
            }}
            data-scheduler='headerTimeline'
          >
            <Stage
              width={dimensions.gridWidth} 
              height={dimensions.timeHeaderHeight}
            >
              <Layer>
                <TimeHeader 
                  startDate={startDate}
                  endDate={endDate}
                  dimensions={dimensions}
                  highlightedDayIndex={hoveredCell.dayIndex}
                />
              </Layer>
            </Stage>
          </div>
        </div>

        <div 
          style={{ 
            position: 'relative',
            width: dimensions.totalWidth, 
            height: adjustedDimensions.totalHeight,
            marginTop: dimensions.timeHeaderHeight,
            margin: 0,
            padding: 0
          }}
          data-scheduler='scrollableContent'
        >
          <CrosshairIndicator 
            hoveredCell={hoveredCell}
            dimensions={dimensions}
            tracksMap={groupedTracks}
          />
          
          {Array.from(groupedTracks.entries()).map(([category, categoryTracks], categoryIndex) => {
            let categoryY = 0;
            for (let i = 0; i < categoryIndex; i++) {
              const [_, prevCategoryTracks] = Array.from(groupedTracks.entries())[i];
              categoryY += prevCategoryTracks.reduce((total, track) => {
                return total + dimensions.trackHeight + (trackMaxOffsets.get(track.id) || 0);
              }, 0) + CATEGORY_HEADER_HEIGHT;
            }

            return (
              <CategorySection 
                key={category}
                category={category}
                categoryTracks={categoryTracks}
                categoryY={categoryY}
                categoryIndex={categoryIndex}
                dimensions={dimensions}
                filterBarsForTrack={filterBarsForTrack}
                handleCellHover={handleCellHover}
                handleCellClick={handleCellClick}
                hoveredCell={hoveredCell}
                startDate={startDate}
                selectedBarId={selectedBarId}
                handleBarSelect={handleBarSelect}
                handleBarChange={handleBarChange}
                handleBarResizeComplete={handleBarResizeComplete}
                handleBarTextClick={handleBarTextClick}
                CATEGORY_HEADER_HEIGHT={CATEGORY_HEADER_HEIGHT}
                barOffsets={barOffsets}
                trackMaxOffsets={trackMaxOffsets}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
});
